/**
 * AI API client for mstream embedded agent.
 *
 * Targets mstream endpoints (proxied via /api):
 *   POST   /api/ai/chat
 *   POST   /api/ai/chat/stream (SSE streaming)
 *   GET    /api/ai/conversations
 *   GET    /api/ai/conversations/{id}
 *   DELETE /api/ai/conversations/{id}
 *   PATCH  /api/ai/conversations/{id} (rename — not yet implemented server-side)
 */

import { tokenStore } from "@/auth/tokenStore";
import { withClock } from "./promptClock";

import type {
  ChatRequest,
  ChatResponse,
  ContextItem,
  Conversation,
  ConversationContext,
  ConversationMode,
  ConversationSummary,
  StreamEvent,
} from "./types";

const BASE = "/api/ai";

function authHeaders(): Record<string, string> {
  const token = tokenStore.getIdToken() ?? tokenStore.getAccessToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const STATUS_MESSAGES: Record<number, string> = {
  401: "Unauthorized — check API credentials.",
  403: "Forbidden.",
  404: "Not found.",
  422: "Invalid request — check required fields.",
  429: "Rate limited — try again later.",
  500: "Internal server error — check mstream logs.",
  502: "Bad gateway — mstream may be unavailable.",
  503: "Service unavailable.",
};

function errorMessageForStatus(status: number, body: string): string {
  return body || STATUS_MESSAGES[status] || `Request failed (${status}).`;
}

async function request<T>(url: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(url, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...authHeaders(),
      ...(init.headers as Record<string, string> | undefined),
    },
  });

  if (!response.ok) {
    const text = await response.text().catch(() => "");
    throw new Error(errorMessageForStatus(response.status, text));
  }

  // DELETE may return empty body
  const text = await response.text();
  return text ? (JSON.parse(text) as T) : ({} as T);
}

// ---------------------------------------------------------------------------
// Chat
// ---------------------------------------------------------------------------

export interface ChatMessageParams {
  message: string;
  context: ContextItem[];
  conversationId?: string;
  allowWrites?: boolean;
  mode?: ConversationMode;
  conversationContext?: ConversationContext;
  serviceName?: string;
  systemPrompt?: string;
}

function toChatRequest(params: ChatMessageParams): ChatRequest {
  return {
    message: params.message,
    conversation_id: params.conversationId,
    service_name: params.serviceName,
    allow_writes: params.allowWrites ?? false,
    mode: params.mode,
    context_id: params.conversationContext,
    context: params.context,
    system_prompt: withClock(params.systemPrompt, new Date()),
  };
}

export async function sendMessage(
  params: ChatMessageParams,
): Promise<ChatResponse> {
  return request<ChatResponse>(`${BASE}/chat`, {
    method: "POST",
    body: JSON.stringify(toChatRequest(params)),
  });
}

// ---------------------------------------------------------------------------
// Streaming Chat
// ---------------------------------------------------------------------------

export interface StreamCallbacks {
  /** Called when the stream initializes with conversation ID */
  onInit?: (conversationId: string) => void;
  /** Called for each text chunk received */
  onText?: (text: string) => void;
  /** Called when a tool call starts */
  onToolCallStart?: (id: string, name: string) => void;
  /** Called when a tool call completes with its result */
  onToolResult?: (id: string, content: string) => void;
  /** Called when a tool requires confirmation (Ask mode) */
  onToolConfirm?: (id: string, name: string, arguments_: unknown) => void;
  /** Called when the stream completes */
  onDone?: () => void;
  /** Called on error */
  onError?: (error: string) => void;
}

/**
 * Send a message using SSE streaming.
 * Returns a function to abort the stream.
 */
export function sendMessageStream(
  params: ChatMessageParams,
  callbacks: StreamCallbacks,
): () => void {
  const controller = new AbortController();

  executeStream(toChatRequest(params), controller.signal, callbacks);

  return () => controller.abort();
}

async function openStreamConnection(
  body: ChatRequest,
  signal: AbortSignal,
): Promise<ReadableStreamDefaultReader<Uint8Array> | string> {
  const response = await fetch(`${BASE}/chat/stream`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify(body),
    signal,
  });

  if (!response.ok) {
    const text = await response.text().catch(() => "");
    return errorMessageForStatus(response.status, text);
  }

  return response.body?.getReader() ?? "No response body";
}

async function executeStream(
  body: ChatRequest,
  signal: AbortSignal,
  callbacks: StreamCallbacks,
) {
  try {
    const result = await openStreamConnection(body, signal);
    if (typeof result === "string") {
      callbacks.onError?.(result);
      return;
    }
    await readSSEStream(result, callbacks);
  } catch (err) {
    if ((err as Error).name === "AbortError") return;
    callbacks.onError?.((err as Error).message ?? "Stream failed");
  }
}

async function readSSEStream(
  reader: ReadableStreamDefaultReader<Uint8Array>,
  callbacks: StreamCallbacks,
) {
  const decoder = new TextDecoder();
  let buffer = "";

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const events = buffer.split("\n\n");
    buffer = events.pop() ?? "";

    for (const event of parseSseEvents(events)) {
      handleStreamEvent(event, callbacks);
    }
  }
}

function parseSseEvents(rawEvents: string[]): StreamEvent[] {
  const parsed: StreamEvent[] = [];
  for (const eventText of rawEvents) {
    if (!eventText.trim()) continue;
    const dataLine = eventText
      .split("\n")
      .find((line) => line.startsWith("data: "));
    if (!dataLine) continue;
    try {
      parsed.push(JSON.parse(dataLine.slice(6)) as StreamEvent);
    } catch {
      // skip malformed events
    }
  }
  return parsed;
}

const streamDispatch: Record<
  StreamEvent["type"],
  (event: never, cb: StreamCallbacks) => void
> = {
  init: (e: StreamEvent & { type: "init" }, cb) =>
    cb.onInit?.(e.conversation_id),
  text: (e: StreamEvent & { type: "text" }, cb) => cb.onText?.(e.content),
  tool_call: (e: StreamEvent & { type: "tool_call" }, cb) =>
    cb.onToolCallStart?.(e.id, e.name),
  tool_result: (e: StreamEvent & { type: "tool_result" }, cb) =>
    cb.onToolResult?.(e.id, e.content),
  tool_confirm: (e: StreamEvent & { type: "tool_confirm" }, cb) =>
    cb.onToolConfirm?.(e.id, e.name, e.arguments),
  done: (_e: StreamEvent & { type: "done" }, cb) => cb.onDone?.(),
  error: (e: StreamEvent & { type: "error" }, cb) => cb.onError?.(e.message),
};

function handleStreamEvent(event: StreamEvent, callbacks: StreamCallbacks) {
  streamDispatch[event.type]?.(event as never, callbacks);
}

// ---------------------------------------------------------------------------
// Conversations
// ---------------------------------------------------------------------------

export async function listConversations(
  mode?: ConversationMode,
  context?: ConversationContext,
): Promise<ConversationSummary[]> {
  const params = new URLSearchParams();
  if (mode) params.set("mode", mode);
  if (context) params.set("context", context);
  const qs = params.toString();
  const url = qs ? `${BASE}/conversations?${qs}` : `${BASE}/conversations`;
  return request<ConversationSummary[]>(url);
}

export async function getConversation(id: string): Promise<Conversation> {
  return request<Conversation>(
    `${BASE}/conversations/${encodeURIComponent(id)}`,
  );
}

export async function deleteConversation(id: string): Promise<void> {
  await request<Record<string, unknown>>(
    `${BASE}/conversations/${encodeURIComponent(id)}`,
    { method: "DELETE" },
  );
}

export interface ConversationRename {
  id: string;
  title: string;
}

/**
 * Rename a conversation. The mstream agent does not implement this endpoint
 * yet (returns 405) — callers must handle rejection and fall back to local
 * persistence until it ships.
 */
export async function renameConversation(
  rename: ConversationRename,
): Promise<void> {
  await request<Record<string, unknown>>(
    `${BASE}/conversations/${encodeURIComponent(rename.id)}`,
    { method: "PATCH", body: JSON.stringify({ title: rename.title }) },
  );
}

export interface ToolCallConfirmation {
  toolCallId: string;
  confirmed: boolean;
}

export async function confirmToolCall(
  confirmation: ToolCallConfirmation,
): Promise<void> {
  await request<Record<string, unknown>>(`${BASE}/chat/confirm`, {
    method: "POST",
    body: JSON.stringify({
      tool_call_id: confirmation.toolCallId,
      confirmed: confirmation.confirmed,
    }),
  });
}
